import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig, type RawAxiosHeaders } from 'axios'
import { z } from 'zod'
import { API_URL } from '../constants'
import { camelCase } from '../helpers'

interface RetryConfig extends InternalAxiosRequestConfig {
  __retryCount?: number
}

export const setHeaders = (headers: RawAxiosHeaders) => {
  API.defaults.headers.common = { ...API.defaults.headers.common, ...headers }
}

export const API = axios.create({
  baseURL: API_URL,
  timeout: 7500,
  headers: {
    'Content-Type': 'application/json',
  },
})

API.interceptors.request.use(
  config => {
    const token = sessionStorage.getItem('jwt')

    if (token) {
      config.headers.set('Authorization', `Bearer ${token}`)
    } else {
      delete API.defaults.headers.common.Authorization
    }

    return config
  },

  error => Promise.reject(error),
)

const retryInterceptor = (instance: AxiosInstance, retries = 3, delayFactor = 1000) => {
  API.interceptors.response.use(null, async (error: AxiosError) => {
    const { config, response } = error

    if (!config) {
      return Promise.reject(error)
    }

    const retryConfig = config as RetryConfig

    if (!response) {
      retryConfig.__retryCount = retryConfig.__retryCount || 0

      const delay = 2 ** (retryConfig.__retryCount + 1) * delayFactor

      if (retryConfig.__retryCount < retries) {
        retryConfig.__retryCount++
        await new Promise(resolve => setTimeout(resolve, delay))
        return instance(config)
      }
    }
    return Promise.reject(error)
  })
}
retryInterceptor(API)

export function handleError(error: unknown, type = 'Unknown') {
  let errorMessage = 'There was an error - please try again.'
  if (error instanceof z.ZodError) {
    errorMessage = error.issues.map(i => `${i.code}: ${i.message} - ${i.path.join('.')}`).join('\n')
  } else if (error instanceof AxiosError) {
    errorMessage = error.response?.data?.message ?? error.message
  } else if (error instanceof Error) {
    errorMessage = error.message
  } else {
    errorMessage = 'There was an error - please try again.'
  }

  console.error(error)
  return Promise.reject(new Error(`${type} - ${errorMessage}`, { cause: error }))
}

export function handleZodError<T extends Record<string, unknown>>(error: z.ZodError<T>) {
  const formatedErrors = error.format()
  const errors: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(formatedErrors)) {
    if (value && !Array.isArray(value)) {
      errors[key] = value._errors[0]
    }
  }
  return errors
}

export function handleConflictError(error: unknown, type = 'Unknown') {
  if (error instanceof AxiosError && error.response?.status === 409) {
    return Promise.resolve({
      error: true,
      message: String(error.response?.data?.errors ?? 'Sorry - Something went wrong.'),
    })
  } else {
    return handleError(error, type)
  }
}

/**
 * Handles Errors on form submissions.
 * Attempts to turn Zod or API errors into something a form can use
 * to show useful errors to the end user
 * @param error
 * @param type
 */
export function handleFormError(error: unknown, type = 'Unknown') {
  if (error instanceof AxiosError) {
    if (error.response?.status === 409) {
      return Promise.resolve({
        error: true,
        message: String(error.response?.data?.errors ?? 'Error Submitting Form'),
        fields: undefined,
      })
    }

    // API validation errors
    // TODO: I'd argue that we should use 422 so we can be sure this is _specifically_ a validation error
    if (error.response?.status === 400) {
      return Promise.resolve({
        error: true,
        message: String(error.response?.data?.error ?? 'Error Submitting Form'),
        fields: error.response?.data?.errors ? apiIssuesToFields(error.response.data.errors) : undefined,
      })
    }

    // fallback
    return handleError(error, type)
  }

  // Zod errors
  if (error instanceof z.ZodError) {
    const fields = zodIssuesToFields(error.issues)
    return Promise.resolve({
      error: true,
      message: 'Error Validating Form',
      fields,
    })
  }

  // fallback
  return handleError(error, type)
}

/** HELPERS */
function zodIssuesToFields(issues: z.ZodIssue[]) {
  return issues.reduce(
    (acc, issue) => {
      // Deep fields will return something like field.0.sub_field
      const field = issue.path.join('.')
      acc[field] = issue.message
      return acc
    },
    {} as Record<string, string>,
  )
}

function apiIssuesToFields(issues: Record<string, string[]>) {
  return Object.entries(issues).reduce(
    (acc, [key, values]) => {
      acc[camelCase(key)] = values.join(' ')
      return acc
    },
    {} as Record<string, string>,
  )
}
